import {
  computed,
  DestroyRef,
  Directive,
  effect,
  inject,
  input,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {RoleCheckService} from '../services/role-check.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ProjectRoleType,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
} from '@frontend/shared-api';
import {UserStateService} from '../services/user-state.service';

export interface ProjectRoleConfig {
  projectId: number;
  roles?: ProjectRoleType[];
  allowTenantAdmin?: boolean;
  userDetail?: ProjectUserDetailResponse;
}

@Directive({
  selector: '[appProjectRoleAccess]',
})
export class ProjectRoleAccessDirective {
  readonly appProjectRoleAccess = input<number | ProjectRoleConfig | null>(null);

  private readonly templateRef = inject(TemplateRef);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly roleCheckService = inject(RoleCheckService);
  private readonly projectUserController = inject(ProjectUserControllerService);
  private readonly userStateService = inject(UserStateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly projectId = signal<number | null>(null);
  private readonly requiredRoles = signal<ProjectRoleType[]>([ProjectRoleType.PROJECT_MANAGER]);
  private readonly allowTenantAdmin = signal<boolean>(true);
  private readonly providedUserDetail = signal<ProjectUserDetailResponse | undefined>(undefined);

  private readonly currentUser = this.userStateService.currentUser;

  private readonly isTenantAdmin = computed(() => this.roleCheckService.hasCustomerAdminRole());

  private readonly fetchedUserDetail = signal<ProjectUserDetailResponse | null>(null);
  private readonly isViewShown = signal<boolean>(false);

  private readonly hasAccess = computed(() => {
    if (this.allowTenantAdmin() && this.isTenantAdmin()) {
      return true;
    }

    const userDetail = this.providedUserDetail() || this.fetchedUserDetail();
    if (!userDetail) return false;

    const userRoles = userDetail.roles ? Array.from(userDetail.roles) : [];
    const roles = this.requiredRoles();

    return roles.some((role) => userRoles.includes(role));
  });

  constructor() {
    effect(() => {
      this.applyAccessValue(this.appProjectRoleAccess());
    });

    effect(() => {
      const projectId = this.projectId();
      const user = this.currentUser();
      const provided = this.providedUserDetail();

      if (provided || !projectId || !user) {
        this.fetchedUserDetail.set(null);
        return;
      }

      this.projectUserController
        .getProjectUserWithDetails({
          projectId,
          userId: user.id,
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (userDetail) => this.fetchedUserDetail.set(userDetail),
          error: () => this.fetchedUserDetail.set(null),
        });
    });

    effect(() => {
      const access = this.hasAccess();
      if (access && !this.isViewShown()) {
        this.showContent();
      } else if (!access && this.isViewShown()) {
        this.hideContent();
      }
    });
  }

  private showContent(): void {
    this.viewContainer.createEmbeddedView(this.templateRef);
    this.isViewShown.set(true);
  }

  private hideContent(): void {
    this.viewContainer.clear();
    this.isViewShown.set(false);
  }

  private applyAccessValue(value: number | ProjectRoleConfig | null): void {
    if (value === null || typeof value === 'number') {
      this.projectId.set(value);
      return;
    }

    this.applyConfig(value);
  }

  private applyConfig(config: ProjectRoleConfig): void {
    this.projectId.set(config.projectId);

    if (config.roles !== undefined) {
      this.requiredRoles.set(config.roles);
    }

    if (config.allowTenantAdmin !== undefined) {
      this.allowTenantAdmin.set(config.allowTenantAdmin);
    }

    if (config.userDetail !== undefined) {
      this.providedUserDetail.set(config.userDetail);
    }
  }
}
